import React, { useEffect, useMemo, useReducer, useCallback, useRef } from 'react';
import PropTypes from 'prop-types';
import { hasValue } from 'utils';
import _get from 'lodash/get';
import isEqual from 'lodash/isEqual';
import RefinerView from './Refiner.view';
import { fieldShape } from './Refiner.props';
import moment from 'moment';
import { useSelector } from 'react-redux';

const actions = {
    ADD: 'ADD',
    CLEAR: 'CLEAR',
    REMOVE: 'REMOVE',
    SET: 'SET',
    RESTORE: 'RESTORE'
};

const blankValue = 'Y2M5NzAzNWEtNGRjYy00Nzc2LTk3ODYtZGVmZjZlOTFjMjE4';

const Refiner = ({
    disabled = false,
    isRemote = true,
    fields: allFields,
    open,
    readOnly = false,
    data,
    onPanelToggleClick,
    onRefinementChange,
    onRefinementSelectorsChange,
    onRefinementSelectorsRemove,
    onRefinementAllClear
}) => {
    const persistedRefiner = useSelector(state => state.contract_globals.refiner);
    const [{selectors, allValid, filter}, dispatch] = useReducer(selectorReducer, { allFields, persistedRefiner }, initState);
    const uniqueDataValues = useMemo(() => getUniqueDataValues(data, allFields), [data, allFields]);
    const onRefinementChangeHandler = useCallback(onRefinementChange, []);
    const availableFields = allFields
        .filter(field => selectors.every(selector => selector.field !== field.name) && hasDataValues(field.name));
    const showAdd = allValid && availableFields.length > 0 && !readOnly;
    const prevData = useRef(data);

    useEffect(() => {    
        if (JSON.stringify(data) !== JSON.stringify(prevData.current)) {
            if (persistedRefiner.selectors.length > 0 && isEqual(persistedRefiner.refinerFields, allFields))
            {
                dispatch({ type: actions.RESTORE, payload: persistedRefiner });
            }
            else
            {
                dispatch({ type: actions.CLEAR });
            }
    
            prevData.current = data;
            const sourceData = data.filter(showAllRows);
            sourceData.name = data.name;
            onRefinementChangeHandler && onRefinementChangeHandler(sourceData);
            onRefinementSelectorsChange && onRefinementSelectorsChange(selectors);
        } else {
            const refinedData = data.filter(filter);
            refinedData.name = filter === showAllRows ? data.name : "Refined";
            onRefinementChangeHandler && onRefinementChangeHandler(refinedData);
            onRefinementSelectorsChange && onRefinementSelectorsChange(selectors, blankValue);    
        }
    }, [data, filter, onRefinementChangeHandler]);

    return (
        <RefinerView
            allFields={allFields}
            availableFields={availableFields}
            disabled={disabled}
            isOpen={open}
            isReadOnly={readOnly}
            selectors={selectors}
            showAdd={showAdd}
            uniqueDataValues={uniqueDataValues}
            onAddClick={useCallback(handleAddClick, [availableFields])}
            onPanelToggleClick={useCallback(onPanelToggleClick, [])}
            onRowFieldChange={useCallback(handleRowFieldChange, [])}
            onRowValueChange={useCallback(handleRowValueChange, [])}
            onRowRemoveClick={useCallback(handleRowRemove, [])}
            onClearAllClick={useCallback(handleClearAll, [])}
        />
    );

    function handleAddClick() {
        const selector = { isValid: false };
        if (availableFields.length === 1) {
            selector.field = availableFields[0].name;
        }
        dispatch({ type: actions.ADD, payload: selector });
    }

    function handleClearAll() {   
        dispatch({ type: actions.CLEAR });
        onRefinementAllClear && onRefinementAllClear();
        clearAll = false;
    }

    function handleRowValueChange(value, selector) {
        dispatch({
            type: actions.SET,
            payload: {
                selector,
                reducer: () => validateSelector({
                    ...selector,
                    value: value
                })
            }
        });
    }

    function handleRowRemove(selector) {
        dispatch({ type: actions.REMOVE, payload: selector });
        onRefinementSelectorsRemove && onRefinementSelectorsRemove();
    }

    function hasDataValues(fieldName) {
        if (isRemote)
        {
            return true;
        }

        const uniqueValues = uniqueDataValues[fieldName];
        return !!(uniqueValues && (uniqueValues === true || uniqueValues.length));
    }

    function handleRowFieldChange(selector, field) {
        dispatch({
            type: actions.SET,
            payload: {
                selector,
                reducer: prevSelector => ({
                    ...prevSelector,
                    field: field.name,
                    value: null,
                    isValid: false
                })
            }
        });
    }

    function validateSelector(selector) {
        var error = null;
        if (selector.field) {
            const selectedField = allFields.find(f => f.name === selector.field);
            const stringValue = typeof (selector.value) === 'string' || typeof (selector.value) === 'date' ? selector.value : '';
            switch (selectedField.type) {
                case 'text':
                    error = stringValue.length ? null : 'Required';
                    break;
                case 'date':
                    error = stringValue.length ? null : 'Required';
                    break;

                case 'list':
                case 'select':
                    error = (selector.value === blankValue && selectedField.allowEmpty) ||
                        (selector.value && selector.value.length) ? null : 'Required';
                    break;

                case 'number-select':
                    error = hasValue(selector.value?.selectedValue) ? null : 'Required';
                    break;

                default:
                    break;
            }
        }

        selector.isValid = !!(selector.field && error === null);
        selector.error = error;
        return selector;
    }
};

function showAllRows() {
    return true;
}

function buildFilter(selectors, allFields) {
    const filters = selectors.map(selector => buildFilterFromSelector(selector, allFields));

    if (filters.every(f => f === null)) {
        return showAllRows;
    }

    return (row) => filters.every(f => f === null || f(row));
}

function buildFilterFromSelector(selector, allFields) {
    if (!selector.isValid) {
        return null;
    }

    const selectedField = allFields.find(f => f.name === selector.field);
    const fieldName = selectedField.name;
    const value = selector.value;
    const matchOnEmpty = selectedField.allowEmpty && Array.isArray(value) && value.indexOf(blankValue) >= 0;

    if (selectedField) {
        if (selectedField.matcher) {
            const matcher = selectedField.matcher;
            return (row) => matcher(row, value);
        }

        if (selectedField.type === 'list' && Array.isArray(value) && value.length) {
            if (matchOnEmpty) {
                return (row) => {
                    const cell = _get(row, fieldName);
                    return !hasValue(cell) || value.indexOf(cell) >= 0;
                }
            }

            return (row) => value.indexOf(_get(row, fieldName)) >= 0;
        }

        if (selectedField.type === 'text' && typeof value === 'string' && value.length) {
            const searchTerm = value.toLowerCase();
            return (row) => {
                const rowValue = _get(row, fieldName);
                return typeof rowValue === 'string' && rowValue.toLowerCase().includes(searchTerm);
            }
        }

        if (selectedField.type === 'date' && typeof value === 'string' && value.length) {
            return (row) => {
                const rowValueX = _get(row, fieldName);
                return typeof rowValueX === 'string' && moment(rowValueX).format('DD/MM/YYYY') === moment(value).format('DD/MM/YYYY');
            }
        }

        if (selectedField.type === 'select' && typeof value === 'string' && value.length) {
            return row => _get(row, fieldName) === value;
        }

        if (selectedField.type === 'number-select') {
            const { selectedValue, andAbove } = value;
            if (andAbove) {
                const selectedNumber = parseInt(selectedValue);
                return row => parseInt(_get(row, fieldName)) >= selectedNumber;
            }

            return row => _get(row, fieldName) === selectedValue;

        }
    }

    return null;
}

function getUniqueDataValues(dataSource, allFields) {

    const uniqueValuesByField = allFields
        .reduce((accumulator, field) => {
            if (field.dataSource) {
 
                if (field.dataSource instanceof Function)
                {
                    return {
                        ...accumulator,
                        [field.name]: field.dataSource(dataSource)
                    };    
                }

                return {
                    ...accumulator,
                    [field.name]: field.dataSource
                };    
            }

            if (field.type === 'list' || field.type === 'select' || field.type === 'number-select') {
                return {
                    ...accumulator,
                    [field.name]: getUniqueValuesByField(dataSource, field.name)
                };
            }

            return {
                ...accumulator,
                [field.name]: hasValuesByField(dataSource, field.name)
            }
        }, {});

    return uniqueValuesByField;
}

function getUniqueValuesByField(dataSource, fieldName) {
    let hasEmptyValues = false;
    const set = new Set(dataSource
        .map(row => _get(row, fieldName))
        .filter(val => {
            if (hasValue(val)) {
                return true;
            }
            hasEmptyValues = true;
            return false;
        }));
    const values = new Array(...set);
    values.sort();
    values.hasEmptyValues = hasEmptyValues;
    return values;
}

function hasValuesByField(dataSource, fieldName) {
    return dataSource.some(row => hasValue(_get(row, fieldName)));
}

function initState(data) {
    const { allFields, persistedRefiner } = data;

    var selectors = [];
    if (persistedRefiner.selectors.length > 0 && isEqual(persistedRefiner.refinerFields, allFields))
    {
        selectors = persistedRefiner.selectors;
    }

    return {
        allFields,
        selectors,
        allValid: true,
        filter: showAllRows,
    };
}

function selectorReducer(state, action) {
    let newState;

    switch (action.type) {
        case actions.ADD:
            const toAdd = action.payload;
            newState = {
                ...state,
                selectors: [...state.selectors, toAdd]
            };
            break;
        case actions.REMOVE:
            const toRemove = action.payload;
            newState = {
                ...state,
                selectors: state.selectors.filter(s => s !== toRemove)
            };
            break;
        case actions.SET:
            const { selector, reducer } = action.payload;
            const index = state.selectors.indexOf(selector);
            newState = {
                ...state,
            };
            newState.selectors[index] = reducer(selector);
            break;
        case actions.CLEAR:
            newState = {
                ...state,
                selectors: []
            };
            break;
        case actions.RESTORE:
            newState = {
                ...state,
                selectors: action.payload.selectors
            };
            break;
        case actions.RESTORE:
            newState = {
                ...state,
                selectors: action.payload.selectors
            };
            break;            
        default:
            break;
    }

    newState.allValid = newState.selectors.every(s => s.isValid);
    newState.filter = buildFilter(newState.selectors, newState.allFields);    

    return newState;
}

Refiner.propTypes = {
    fields: PropTypes.arrayOf(PropTypes.shape(fieldShape)).isRequired,
    open: PropTypes.bool,
    readOnly: PropTypes.bool,
    data: PropTypes.array.isRequired,
    onPanelToggleClick: PropTypes.func,
    onRefinementChange: PropTypes.func,
};

export default Refiner;